import { ApolloClient } from '@apollo/client';
import { queryRegistry } from 'src/graphql/setup/query-registry';
import { isEntityRelevantToFilters } from 'src/graphql/setup/utils/isEventRelevant';
import { ConditionInput } from 'src/graphql/types';
import { NotificationRaw } from 'src/types';
import { GetNotification } from './hooks';
import { NotificationChannelType } from './notification.types';

export function getBaseNotificationFilters(userId: string): ConditionInput[] {
  return [
    {
      attribute: 'userId',
      operator: '=',
      value: userId,
    },
    {
      attribute: 'notificationChannelId',
      operator: '=',
      value: NotificationChannelType.InApp,
    },
  ];
}

// reads the cached records from Apollo using queryRegistry, filters them by the given conditions,
// and returns those items for use in an optimistic response
export function getOptimisticItemsByFilters(
  filters: ConditionInput[],
  apolloClient: ApolloClient<unknown>,
): NotificationRaw[] {
  const registeredQueries = queryRegistry.getQueriesForEntity('Notification');

  // pull every record out of each cached query
  const allRecords: NotificationRaw[] = [];

  for (const { query, variables } of registeredQueries) {
    try {
      const data = apolloClient.readQuery<GetNotification>({ query, variables });

      if (data?.getNotification.records) {
        allRecords.push(...data.getNotification.records);
      }
    } catch (e) {
      // if a particular query isn't in the cache yet, ignore it
      console.error(e);
    }
  }

  // dedupe by id
  const seen = new Set<string>();

  const unique = allRecords.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });

  // only keep the ones that actually match filters
  return unique.filter((item) => isEntityRelevantToFilters(item, filters));
}

// composite key helpers for building grouping key
export const NOTIFICATION_ENTITY_KEY_SEPARATOR = '__';

export function buildNotificationCompositeKey(
  entity?: string | number,
  entityId?: string | number,
): string {
  return [entity, entityId].join(NOTIFICATION_ENTITY_KEY_SEPARATOR);
}

export function parseNotificationCompositeKey(
  key?: string,
): { entity: string; entityId: string } | null {
  if (!key) return null;

  if (!key.includes(NOTIFICATION_ENTITY_KEY_SEPARATOR)) {
    // backward-compat: treat the whole key as entityId if no separator
    return { entity: '', entityId: String(key) };
  }
  const [entity, entityId] = key.split(NOTIFICATION_ENTITY_KEY_SEPARATOR);

  return { entity: String(entity ?? ''), entityId: String(entityId ?? '') };
}
